import { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router'
import { Stack, Heading, Text, Button, Alert, FormControl, Textarea, Checkbox, Loading, Badge, Tag } from '@/components/lib'
import api from '../api'
import { FilePreview } from './trials/FileViewer'

export default function FileApproval () {
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [trialName, setTrialName] = useState('')
  const [message, setMessage] = useState('')
  const [fileName, setFileName] = useState('')
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [version, setVersion] = useState('')
  const [hash, setHash] = useState('')
  const [approved, setApproved] = useState(false)
  const [rejected, setRejected] = useState(false)
  const [comment, setComment] = useState('')

  const { fileId, approvalId } = useParams()

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    return params.get('token')
  }, [])

  useEffect(() => {
    if (!fileId || !approvalId) setError('Details could not be found')
    else {
      setLoading(true)
      api.files.getApproverApproval(fileId, approvalId, token, data => {
        setTrialName(data.trialName)
        setMessage(data.message)
        setFileName(data.fileName)
        setName(data.name)
        setUrl(data.url)
        setVersion(data.version)
        setHash(data.hash)
        setLoading(false)
      }, err => {
        setLoading(false)
        setError('There was an error fetching the approval information: ' + err.message)
      })
    }
  }, [fileId, approvalId])

  function submit () {
    setError('')
    if (!approved && !rejected) {
      setError('You must either approve or reject the file.')
      return
    }
    if (rejected && !comment?.length) {
      setError('You must provide a comment if you do not approve the file.')
      return
    }

    setLoading(true)
    api.files.updateApproverApproval(fileId, approvalId, { comment, approved }, token, data => {
      setLoading(false)
      setSuccess(true)
    }, err => {
      setLoading(false)
      setError('There was an error submitting your approval decision: ' + err.message)
    })
  }

  if (success) {
    return (
      <Stack gap={10} my={10} maxW='500px' mx='auto'>
        <Heading size='lg' textAlign='center'>File Approval</Heading>
        <Alert status='success' textAlign='center'>Thank you. Your approval decision has been submitted successfully. You can now close this page.</Alert>
      </Stack>
    )
  }

  if (!trialName || !fileName || !name || !url || !version) {
    return (
      <Stack gap={10} my={10} maxW='500px' mx='auto'>
        <Heading size='lg' textAlign='center'>File Approval</Heading>
        {loading && <Loading />}
        {!loading && error &&
          <Alert status='error' textAlign='center'>{error}</Alert>}
        {!loading && !error &&
          <Alert status='info' textAlign='center'>There was an error fetching the approval information. Please try again later.</Alert>}
      </Stack>
    )
  }

  return (
    <Stack gap={10} my={10} maxW='960px' mx='auto' px={4}>
      <Stack bg='bg.subtle' p={5} borderRadius='md' gap={5}>
        <Heading size='lg' textAlign='center'>File Approval</Heading>
        <Text textAlign='center'>You have been asked to review the document below for inclusion in the eTMF of {trialName}.</Text>
      </Stack>

      {message && message.length > 0 &&
        <Stack bg='bg.accentSubtle' p={5} borderRadius='md' gap={3}>
          <Heading size='md'>Message from the trial coordinator</Heading>
          <Text whiteSpace='pre'>{message}</Text>
        </Stack>}

      <Stack bg='bg.muted' p={3} borderRadius='md' gap={3}>
        <Heading size='md'>File details</Heading>
        <Stack direction='row' alignItems='center' gap={3}>
          <Text fontFamily='monospace' fontWeight='bold'>{name} ({fileName})</Text>
          <Tag colorPalette='gray'><Text textTransform='uppercase'>{fileName?.split('.').pop()}</Text></Tag>
          <Badge colorPalette='blue'>Version {version}</Badge>
        </Stack>
      </Stack>

      {hash && hash.length > 0 &&
        <Stack bg='bg.muted' p={3} borderRadius='md'>
          <Heading size='md'>File hash</Heading>
          <Text fontSize='sm'>This is a SHA256 hash of the file contents. You can use this to verify the file has not been altered.</Text>
          <Text fontFamily='monospace' wordBreak='break-all'>{hash}</Text>
        </Stack>}

      <Stack gap={3}>
        <Heading size='md'>Document preview</Heading>
        <FilePreview
          fileName={fileName}
          downloadUrl={url}
          previewKey={`${fileId}:${approvalId}`}
          loadPreview={(ok, fail) => api.files.getApproverPreview(fileId, approvalId, token, ok, fail)}
        />
      </Stack>

      <Button as='a' href={url} target='_blank' rel='noopener noreferrer' colorPalette='secondary'>Download the original file</Button>

      <Stack direction='row' gap={3} alignItems='center'>
        <Checkbox
          isChecked={approved} onChange={e => {
            setApproved(e.checked)
            setRejected(false)
          }}
        >
          I <strong>approve</strong> this file for inclusion in the eTMF
        </Checkbox>

        <Checkbox
          isChecked={rejected} onChange={e => {
            setRejected(e.checked)
            setApproved(false)
          }}
        >
          I <strong>do not approve</strong> this file for inclusion in the eTMF
        </Checkbox>
      </Stack>

      <FormControl label='Comment' helperText='This is required if you do not approve the document.'>
        <Textarea placeholder='Type your comment here...' value={comment} onChange={e => setComment(e.target.value)} />
      </FormControl>

      {error && <Alert status='error'>{error}</Alert>}

      <Button colorPalette='primary' isLoading={loading} onClick={submit}>Submit approval decision</Button>

    </Stack>
  )
};
